/** Кольцевые буферы для баг-репорта доски: flash, ops, действия, снимок сцены. */

const MAX_FLASH = 12;
const MAX_OPS = 12;
const MAX_ACTIONS = 16;

type FlashRec = { ts: string; kind: string; text: string; code?: string; context?: string };
type OpsRec = {
  ts: string;
  ok: boolean;
  code?: string;
  message?: string;
  httpStatus?: number;
  clientOpId?: string;
  opTypes?: string;
};
type ActionRec = { ts: string; name: string; detail?: string };

const flashBuf: FlashRec[] = [];
const opsBuf: OpsRec[] = [];
const actionBuf: ActionRec[] = [];

type SceneGetter = () => {
  revision: number;
  objects: Array<{ type?: string }>;
} | null;

let sceneGetter: SceneGetter | null = null;

function nowTs(): string {
  return new Date().toISOString().slice(11, 23);
}

function pushRing<T>(buf: T[], item: T, max: number) {
  buf.push(item);
  while (buf.length > max) buf.shift();
}

export function noteDiagFlash(kind: string, text: string) {
  const t = String(text || "").trim().slice(0, 240);
  if (!t) return;
  pushRing(flashBuf, { ts: nowTs(), kind, text: t }, MAX_FLASH);
}

/** Структурированная ошибка сохранения — дублирует flash и пишет code/context в экспорт. */
export function noteDiagBoardError(opts: {
  text: string;
  code?: string;
  context?: string;
}) {
  const t = String(opts.text || "").trim().slice(0, 240);
  if (!t) return;
  pushRing(
    flashBuf,
    {
      ts: nowTs(),
      kind: "error",
      text: t,
      code: opts.code ? String(opts.code).slice(0, 64) : undefined,
      context: opts.context ? String(opts.context).slice(0, 64) : undefined,
    },
    MAX_FLASH
  );
}

export function noteDiagOps(opts: {
  ok: boolean;
  code?: string;
  message?: string;
  httpStatus?: number;
  clientOpId?: string;
  ops?: Array<{ type?: string }>;
}) {
  const opTypes = (opts.ops || [])
    .map((o) => o?.type || "?")
    .slice(0, 8)
    .join(",");
  pushRing(
    opsBuf,
    {
      ts: nowTs(),
      ok: opts.ok,
      code: opts.code ? String(opts.code).slice(0, 64) : undefined,
      message: opts.message ? String(opts.message).slice(0, 200) : undefined,
      httpStatus: opts.httpStatus,
      clientOpId: opts.clientOpId ? String(opts.clientOpId).slice(0, 80) : undefined,
      opTypes: opTypes || undefined,
    },
    MAX_OPS
  );
}

export function noteDiagAction(name: string, detail?: string) {
  pushRing(
    actionBuf,
    {
      ts: nowTs(),
      name: String(name).slice(0, 64),
      detail: detail ? String(detail).slice(0, 120) : undefined,
    },
    MAX_ACTIONS
  );
}

export function setBoardDiagSceneGetter(getter: SceneGetter | null) {
  sceneGetter = getter;
}

export function getDiagFlashExport(): string {
  if (!flashBuf.length) return "(нет flash-сообщений)";
  return flashBuf
    .map((f) => {
      const meta = [f.code ? `code=${f.code}` : null, f.context ? `ctx=${f.context}` : null]
        .filter(Boolean)
        .join(" ");
      return `${f.ts} [${f.kind}] ${f.text}${meta ? ` (${meta})` : ""}`;
    })
    .join("\n");
}

export function getDiagOpsExport(): string {
  if (!opsBuf.length) return "(нет ops в этой сессии)";
  return opsBuf
    .map((o) => {
      const parts = [
        o.ts,
        o.ok ? "ok" : "FAIL",
        o.httpStatus ? `http=${o.httpStatus}` : null,
        o.code ? `code=${o.code}` : null,
        o.opTypes ? `ops=${o.opTypes}` : null,
        o.clientOpId ? `id=${o.clientOpId}` : null,
        o.message || null,
      ].filter(Boolean);
      return parts.join(" ");
    })
    .join("\n");
}

export function getDiagActionsExport(): string {
  if (!actionBuf.length) return "(нет действий)";
  return actionBuf
    .map((a) => `${a.ts} ${a.name}${a.detail ? ` ${a.detail}` : ""}`)
    .join("\n");
}

export function getDiagSceneSnapshotExport(): string {
  try {
    const snap = sceneGetter?.() ?? null;
    if (!snap) return "(сцена недоступна)";
    const counts: Record<string, number> = {};
    for (const obj of snap.objects || []) {
      const t = typeof obj?.type === "string" ? obj.type : "unknown";
      counts[t] = (counts[t] || 0) + 1;
    }
    const byType = Object.keys(counts)
      .sort()
      .map((k) => `${k}:${counts[k]}`)
      .join(", ");
    return `revision=${snap.revision}; objects=${snap.objects.length}${
      byType ? `; types={${byType}}` : ""
    }`;
  } catch {
    return "(ошибка чтения сцены)";
  }
}
